import * as React from 'react';
import { FieldGroup, FieldLabel, Input, Modal, Button } from '@teamsnap/teamsnap-ui';
import { useQuery } from '@apollo/client';

import * as GqlPrograms from '../../core/gql/queries/programs';
import * as GqlSeasons from '../../core/gql/queries/seasons';
import * as GqlForms from '../../core/gql/queries/get-all-forms-reg';
import Program from '../../models/program';
import Season from '../../models/season';

export enum FormOperation {
  Duplicate,
  New,
}

interface OnSubmitPayload {
  program: Program;
  season: Season;
  newFormName?: string;
}

export interface Props {
  type: FormOperation;
  orgId: number;
  show: boolean;
  onSubmit: (payload: OnSubmitPayload) => void;
  onCancel: () => void;
  heading: React.ReactNode;
  subheading: React.ReactNode;
  submitButtonText?: string;
  testId?: string;
}

const FormModal: React.FC<Props> = ({
  type,
  orgId,
  show,
  onSubmit,
  onCancel,
  heading,
  subheading,
  submitButtonText = 'Save',
  testId = null,
}) => {
  const [selectedProgram, setSelectedProgram] = React.useState<Program>(null);
  const [selectedSeason, setSelectedSeason] = React.useState<Season>(null);
  const [newFormName, setNewFormName] = React.useState<string>('');
  const [newFormNameTouched, setNewFormNameTouched] = React.useState<boolean>(false);
  const [seasonsToSkip, setSeasonsToSkip] = React.useState<number[]>(null);

  const queryForms = useQuery(GqlForms.QUERY, { variables: { organizationId: orgId } });

  const programs: Program[] = [
    ...(useQuery(GqlPrograms.QUERY, {
      variables: { organizationId: orgId },
    })?.data?.programs || []),
  ].sort((a, b) => b.id - a.id);

  const seasons: Season[] = [
    ...(useQuery(GqlSeasons.QUERY, {
      variables: { programId: selectedProgram?.id },
      skip: !selectedProgram?.id,
    })?.data?.seasons || []),
  ]
    .filter((season) => seasonsToSkip?.indexOf(season.id) === -1)
    .sort((a, b) => b.id - a.id);

  React.useEffect(() => {
    if (queryForms?.data && !seasonsToSkip) {
      const ids: number[] = [];

      queryForms.data.forms.forEach((form) => {
        if (form.isReg2) {
          ids.push(form.season.id);
        }
      });

      setSeasonsToSkip(Array.from(new Set(ids)));
    }
  }, [queryForms, seasonsToSkip]);

  React.useEffect(() => {
    if (!newFormNameTouched) {
      const newFormName = selectedProgram && selectedSeason ? `${selectedProgram?.name} ${selectedSeason?.name}` : '';
      setNewFormName(newFormName);
    }
  }, [newFormNameTouched, selectedProgram, selectedSeason]);

  const resetFields = () => {
    setSelectedProgram(null);
    setSelectedSeason(null);
    setNewFormName('');
  };

  return (
    <div className="duplicate-form-modal">
      <Modal heading={heading} show={show} closeButton={null} closeFn={onCancel} testId={testId}>
        {subheading}

        <FieldGroup>
          <FieldLabel name="program">Program</FieldLabel>

          <div className="SelectBox SelectBox--large">
            <select
              className="SelectBox-options"
              data-testid="form-modal-program-select"
              id="program"
              name="program"
              value={selectedProgram ? selectedProgram?.id : ''}
              disabled={!programs.length}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const program = programs?.find((program) => program.id === +e.target.value);
                setSelectedProgram(program);
                setSelectedSeason(null);
              }}
            >
              <option value="" disabled>
                Select
              </option>

              {programs.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.name}
                </option>
              ))}
            </select>
          </div>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel name="season">Season</FieldLabel>

          <div className="SelectBox SelectBox--large">
            <select
              className="SelectBox-options"
              id="season"
              name="season"
              value={selectedSeason ? selectedSeason?.id : ''}
              disabled={!seasons.length}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const season = seasons?.find((season) => season.id === +e.target.value);
                setSelectedSeason(season);
              }}
            >
              <option value="" disabled>
                Select
              </option>

              {!!seasons.length &&
                seasons.map((season) => (
                  <option key={season.id} value={season.id}>
                    {season.name}
                  </option>
                ))}
            </select>
          </div>
        </FieldGroup>

        {type === FormOperation.Duplicate && (
          <FieldGroup>
            <FieldLabel name="new-form-name">New Form Name</FieldLabel>

            <Input
              name="new-form-name"
              type="text"
              size="large"
              inputProps={{
                value: newFormName,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  setNewFormNameTouched(true);
                  setNewFormName(e.currentTarget.value);
                },
              }}
            />
          </FieldGroup>
        )}

        <div className="btn-group">
          <Button
            size="large"
            onClick={() => {
              resetFields();
              onCancel();
            }}
          >
            Cancel
          </Button>

          <Button
            className="Button save-btn"
            size="large"
            color="primary"
            isDisabled={!(selectedProgram && selectedSeason && newFormName)}
            onClick={() => {
              resetFields();
              onSubmit({
                program: selectedProgram,
                season: selectedSeason,
                newFormName,
              });
            }}
          >
            {submitButtonText}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default FormModal;
